import getEditDistance from 'js-levenshtein'
import forOwn from 'mout/object/forOwn'
import values from 'mout/object/values'
import { getAllTeacherNames } from './'

/**
 * Produces verification errors and warnings related to teacher fields
 * @param {array} uploadData The parsed array of row objects from the csv upload
 * @param {object} records all of the db records we got from initVerification (e.g. students, educators, classes, groupUAs, and userAssessments)
 * @returns {object} {teacherWarnings, teacherErrors}
 */
export default function verifyTeachers (uploadData, records) {
  const teacherWarnings = []
  const teacherErrors = []

  // aggregate all of the teacher names
  const { teacherNamesOnly, teacherNames } = getAllTeacherNames(uploadData)
  
  // check for any abbreviations of teacher names and close edit distance
  teacherNamesOnly.forEach(name => {
    if (!name || name.length > 5) return
    if (name.length < 2 ) {
      const warning = {
        warning: `Teacher name ${name} is very short.`,
        rows: teacherNames[name],
      }
      return teacherWarnings.push(warning)
    }
    //check for abbreviations
    const substringMatch = teacherNamesOnly.filter(tName => tName.substring(0, name.length) === name && tName.length > 1)
    if (substringMatch.length > 1) {
      const warning = {
        warning: `Teacher name ${name} seems to be a shortened version of ${substringMatch[1]}.`,
        rows: teacherNames[name], 
      }
      teacherWarnings.push(warning)
    } else {
      // check for close edit distance among names starting with same letter
      const startingLetterMatch = teacherNamesOnly.filter(tName => tName[0] === name[0] && tName !== name && tName.length > 1)
      if (startingLetterMatch.length > 0) {
        startingLetterMatch.some(tName => {
          const editDistance = getEditDistance(name, tName)
          if (editDistance < 2) {
            const warning = {
              warning: `Teacher name ${name} is very similar to ${tName} and might be the same person.`,
              rows: teacherNames[name],
            }
            teacherWarnings.push(warning)
            return true
          } else return false
        })
      }
    }
  })

  // Check manual input names against existing teacher names: same substring for last name or edit distance < 2
  teacherNamesOnly.forEach(name => {
    if (!name || name.length > 5 || name.length < 2) return
    forOwn(records.educators, val => {
      if (name[0].toLowerCase() === val.lastName[0].toLowerCase()) {
        const lastNameSub = val.lastName.substring(0, name.length).toLowerCase()
        const equalQ = lastNameSub === name.toLowerCase()
        let similarQ
        if (!equalQ) similarQ = getEditDistance(lastNameSub, name.toLowerCase()) < 2
        if (equalQ || similarQ) {
          const warning = {
            warning: `Teacher name ${name} is very similar to 
                      an existing user, ${val.fullName} with 
                      ${val.email ? 'email' : 'username'} 
                      ${val.email || val.username}.`,
            rows: teacherNames[name],
          }
          teacherWarnings.push(warning)
        }
      }
    })
  })

  // If a cell has teacher name that is 6 or more characters, check the name against existing email and usernames.
  // Flag if has a close edit distance and starts with the same letter
  teacherNamesOnly.filter(n => n.length > 5).forEach(name => {
    const educators = values(records.educators)
    const nameLC = name.toLowerCase()
    const firstLetterMatch = ed => {
      return (
        ed.email[0] && ed.email[0].toLowerCase() === nameLC[0] || 
        ed.username[0] && ed.username[0].toLowerCase() === nameLC[0]
      )
    }
    const possibleMatches = educators.filter(firstLetterMatch)
    possibleMatches.forEach(match => {
      const usernameLC = match.username.toLowerCase()
      const emailLC = match.email.toLowerCase()
      const usernameDistanceQ = getEditDistance(usernameLC, nameLC) < 3
      const emailDistanceQ = getEditDistance(emailLC, nameLC) < 3
      if (usernameDistanceQ || emailDistanceQ) {
        const warning = {
          warning: `Teacher identifier ${name} is very similar to 
                    an existing user, ${usernameDistanceQ ? match.username : match.email}
                    and may be the same person.`,
          rows: teacherNames[name],
        }
        teacherWarnings.push(warning)
      }
    })
    
  })
  
  return {teacherWarnings, teacherErrors}
}


// WEBPACK FOOTER //
// ./src/helpers/assessment/uploadCSV/verifyTeachers.js